import React, { useState } from 'react';
import { X, Calendar, Tag, Hash, CheckCircle, Clock, Image as ImageIcon, Layers, FileText } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode, Navigation, Thumbs, EffectFade, Autoplay } from 'swiper/modules';

import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';
import 'swiper/css/effect-fade';

const ViewProjectModal = ({ isOpen, onClose, project, categories }) => {
    const [thumbsSwiper, setThumbsSwiper] = useState(null);

    if (!isOpen || !project) return null;

    const getCategoryName = (catId) => {
        const cat = categories.find(c => c._id === catId);
        return cat ? cat.name : 'Unknown';
    };

    const statusColors = {
        'Ongoing': 'bg-blue-100 text-blue-800',
        'Completed': 'bg-green-100 text-green-800',
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm">
            {/* Modal Container */}
            <div className="bg-white rounded-xl shadow-2xl w-[95%] max-w-3xl overflow-hidden animate-fade-in-up flex flex-col max-h-[90vh]">

                {/* Header */}
                <div className="flex justify-between items-center p-4 md:p-6 border-b border-gray-100 bg-gray-50 flex-shrink-0">
                    <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                        <Layers className="w-5 h-5 text-blue-600" />
                        Project Details
                    </h3>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-200 rounded-full transition-colors text-gray-500 hover:text-gray-700"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="p-4 md:p-8 space-y-8 overflow-y-auto custom-scrollbar">

                    {/* Gallery Section - Swiper Slider */}
                    <div className="space-y-6">
                        {project.images && project.images.length > 0 ? (
                            <>
                                <div className="w-full h-[300px] md:h-[400px]  rounded-2xl overflow-hidden relative group">
                                    <Swiper
                                        style={{
                                            '--swiper-navigation-color': '#fff',
                                            '--swiper-pagination-color': '#fff',
                                            '--swiper-navigation-size': '20px',
                                        }}
                                        loop={true}
                                        effect={'fade'}
                                        autoplay={{
                                            delay: 2000,
                                            disableOnInteraction: false,
                                        }}
                                        spaceBetween={0}
                                        navigation={true}
                                        thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
                                        modules={[FreeMode, Navigation, Thumbs, EffectFade, Autoplay]}
                                        className="mySwiper2 w-full h-full"
                                    >
                                        {project.images.map((img, idx) => (
                                            <SwiperSlide key={idx} className="flex items-center justify-center bg-white/5">
                                                <div className="w-full h-full relative">
                                                    {/* Blurred Background */}
                                                    <div
                                                        className="absolute inset-0 bg-cover bg-center"
                                                        style={{ backgroundImage: `url(${img.url})` }}
                                                    />
                                                    {/* Main Image */}
                                                    {/* <img
                                                        src={img.url}
                                                        alt={`${project.title} - ${idx + 1}`}
                                                        className="absolute inset-0 w-full h-full object-contain z-10"
                                                    /> */}
                                                </div>
                                            </SwiperSlide>
                                        ))}
                                    </Swiper>
                                </div>

                                {/* Thumbnail Swiper */}
                                {project.images.length > 1 && (
                                    <Swiper
                                        onSwiper={setThumbsSwiper}
                                        loop={true}
                                        spaceBetween={12}
                                        slidesPerView={4}
                                        freeMode={true}
                                        watchSlidesProgress={true}
                                        modules={[FreeMode, Navigation, Thumbs]}
                                        className="mySwiper thumbs-gallery !p-2"
                                        breakpoints={{
                                            640: { slidesPerView: 5 },
                                            768: { slidesPerView: 6 },
                                            1024: { slidesPerView: 7 },
                                        }}
                                    >
                                        {project.images.map((img, idx) => (
                                            <SwiperSlide key={idx} className="aspect-square cursor-pointer rounded-xl overflow-hidden transition-all opacity-40 hover:opacity-100 [&.swiper-slide-thumb-active]:opacity-100 [&.swiper-slide-thumb-active]:ring-2 [&.swiper-slide-thumb-active]:ring-primary [&.swiper-slide-thumb-active]:ring-offset-2">
                                                <img
                                                    src={img.url}
                                                    alt={`Thumb ${idx}`}
                                                    className="w-full h-full object-cover"
                                                />
                                            </SwiperSlide>
                                        ))}
                                    </Swiper>
                                )}
                            </>
                        ) : (
                            <div className="w-full h-[300px] bg-gray-50 rounded-2xl flex flex-col items-center justify-center text-gray-400 border border-gray-100">
                                <ImageIcon className="w-16 h-16 mb-3 opacity-20" />
                                <span className="text-sm font-medium opacity-60">No Image Available</span>
                            </div>
                        )}
                    </div>

                    {/* Info Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Title */}
                        <div className="md:col-span-2">
                            <h2 className="text-2xl font-bold text-gray-800 mb-1">{project.title}</h2>
                            <p className="text-sm text-gray-500 flex items-center gap-2">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider ${statusColors[project.status] || 'bg-gray-100 text-gray-800'}`}>
                                    {project.status}
                                </span>
                                <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                                <span className="font-mono text-gray-400">#{project.sku}</span>
                            </p>
                        </div>

                        {/* Category */}
                        <div className="flex items-start gap-3">
                            <div className="p-2 bg-purple-50 text-purple-600 rounded-lg">
                                <Tag className="w-5 h-5" />
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Category</p>
                                <p className="text-gray-800 font-medium">{getCategoryName(project.category)}</p>
                            </div>
                        </div>

                        {/* Started Date */}
                        <div className="flex items-start gap-3">
                            <div className="p-2 bg-orange-50 text-orange-600 rounded-lg">
                                <Calendar className="w-5 h-5" />
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Started Date</p>
                                <p className="text-gray-800 font-medium">
                                    {new Date(project.startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </p>
                            </div>
                        </div>

                        {/* Updated Date */}
                        <div className="flex items-start gap-3">
                            <div className="p-2 bg-pink-50 text-pink-600 rounded-lg">
                                <Clock className="w-5 h-5" />
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Last Updated</p>
                                <p className="text-gray-800 font-medium">
                                    {new Date(project.updatedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </p>
                            </div>
                        </div>

                        {/* SKU/ID (Additional) */}
                        <div className="flex items-start gap-3">
                            <div className="p-2 bg-teal-50 text-teal-600 rounded-lg">
                                <Hash className="w-5 h-5" />
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Project SKU</p>
                                <p className="text-gray-800 font-medium">{project.sku}</p>
                            </div>
                        </div>
                    </div>

                    {/* Description */}
                    <div className="bg-gray-50 p-6 rounded-xl border border-gray-100">
                        <div className="flex items-center gap-2 mb-3 text-gray-500">
                            <FileText className="w-4 h-4" />
                            <h4 className="text-sm font-semibold uppercase tracking-wider">Description</h4>
                        </div>
                        <p className="text-gray-700 leading-relaxed whitespace-pre-wrap text-sm md:text-base">
                            {project.description}
                        </p>
                    </div>

                </div>

                {/* Footer */}
                {/* <div className="p-6 bg-gray-50 border-t border-gray-200 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-6 py-2 bg-gray-800 text-white font-medium rounded-lg hover:bg-gray-900 transition-colors shadow-lg shadow-gray-200"
                    >
                        Close
                    </button>
                </div> */}
            </div>
        </div>
    );
};

export default ViewProjectModal;
